import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { ROLES, FEATURES, hasPermission } from "../lib/rolesConfig";
import {
  FaTrash,
  FaPlus,
  FaSave,
  FaArrowLeft,
  FaEdit,
  FaTimes,
  FaArchive,
  FaUndo,
} from "react-icons/fa";

const DEFAULT_VITALS = [
  {
    id: "bloodPressure",
    name: "Blood Pressure",
    unit: "mmHg",
    enabled: true,
    isDefault: true,
    type: "systolic/diastolic",
    archived: false,
  },
  {
    id: "heartRate",
    name: "Heart Rate",
    unit: "BPM",
    enabled: true,
    isDefault: true,
    archived: false,
  },
  {
    id: "temperature",
    name: "Temperature",
    unit: "°C",
    enabled: true,
    isDefault: true,
    archived: false,
  },
  {
    id: "respiratoryRate",
    name: "Respiratory Rate",
    unit: "BPM",
    enabled: true,
    isDefault: true,
    archived: false,
  },
  {
    id: "o2Sat",
    name: "O2 Saturation",
    unit: "%",
    enabled: true,
    isDefault: true,
    archived: false,
  },
  {
    id: "bloodSugar",
    name: "Blood Sugar",
    unit: "mg/dL",
    enabled: true,
    isDefault: true,
    archived: false,
  },
];

const VitalsSettingsPage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const {
    currentUser,
    userRole,
    activeCareProfile,
    loading: authLoading,
  } = useAuth();
  const [vitalsConfig, setVitalsConfig] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newVitalName, setNewVitalName] = useState("");
  const [newVitalUnit, setNewVitalUnit] = useState("");
  const [editingVitalId, setEditingVitalId] = useState(null);
  const [editingVitalName, setEditingVitalName] = useState("");
  const [editingVitalUnit, setEditingVitalUnit] = useState("");
  const [canManage, setCanManage] = useState(false);

  useEffect(() => {
    if (authLoading) {
      return; // Wait for authentication and user roles to be loaded
    }

    let effectiveRole = userRole?.[profileId];
    if (activeCareProfile?.userId === currentUser?.uid) {
      effectiveRole = ROLES.OWNER;
    }

    const hasManagePermission = hasPermission(
      effectiveRole,
      FEATURES.VITALS,
      "manage",
    );
    setCanManage(hasManagePermission);

    const fetchConfig = async () => {
      if (!profileId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const profileRef = doc(db, "careProfiles", profileId);
      const docSnap = await getDoc(profileRef);
      if (docSnap.exists() && docSnap.data().vitalsConfig) {
        const fetchedConfig = docSnap.data().vitalsConfig.map((vital) => ({
          ...vital,
          archived: vital.archived || false,
        }));
        setVitalsConfig(fetchedConfig);
      } else {
        setVitalsConfig(DEFAULT_VITALS);
      }
      setIsLoading(false);
    };
    fetchConfig();
  }, [profileId, userRole, authLoading, currentUser, activeCareProfile]);

  const handleToggleVital = (id) => {
    if (!canManage) return;
    setVitalsConfig((currentConfig) =>
      currentConfig.map((vital) =>
        vital.id === id ? { ...vital, enabled: !vital.enabled } : vital,
      ),
    );
  };

  const handleAddVital = () => {
    if (!canManage) return;
    if (!newVitalName.trim()) {
      window.alert("Please enter a name for the new vital sign.");
      return;
    }
    const newVital = {
      id:
        newVitalName.trim().toLowerCase().replace(/\s+/g, "_") +
        `_${Date.now()}`,
      name: newVitalName.trim(),
      unit: newVitalUnit.trim(),
      enabled: true,
      isDefault: false,
      archived: false,
    };
    setVitalsConfig([...vitalsConfig, newVital]);
    setNewVitalName("");
    setNewVitalUnit("");
  };

  const handleArchiveVital = (id) => {
    if (!canManage) return;
    if (
      window.confirm(
        "Are you sure you want to archive this vital? It will be hidden from the entry form.",
      )
    ) {
      setVitalsConfig((currentConfig) =>
        currentConfig.map((vital) =>
          vital.id === id
            ? { ...vital, archived: true, enabled: false }
            : vital,
        ),
      );
    }
  };

  const handleReinstateVital = (id) => {
    if (!canManage) return;
    setVitalsConfig((currentConfig) =>
      currentConfig.map((vital) =>
        vital.id === id ? { ...vital, archived: false } : vital,
      ),
    );
  };

  const handleDeleteVitalPermanently = (id) => {
    if (!canManage) return;
    if (
      window.confirm(
        "Are you sure you want to permanently delete this vital? This action cannot be undone.",
      )
    ) {
      setVitalsConfig(vitalsConfig.filter((vital) => vital.id !== id));
    }
  };

  const handleEditClick = (vital) => {
    if (!canManage) return;
    setEditingVitalId(vital.id);
    setEditingVitalName(vital.name);
    setEditingVitalUnit(vital.unit);
  };

  const handleCancelEdit = () => {
    setEditingVitalId(null);
    setEditingVitalName("");
    setEditingVitalUnit("");
  };

  const handleUpdateVital = () => {
    if (!canManage) return;
    if (!editingVitalName.trim()) {
      window.alert("Vital name cannot be empty.");
      return;
    }
    setVitalsConfig((currentConfig) =>
      currentConfig.map((vital) =>
        vital.id === editingVitalId
          ? {
              ...vital,
              name: editingVitalName.trim(),
              unit: editingVitalUnit.trim(),
            }
          : vital,
      ),
    );
    handleCancelEdit();
  };

  const handleSaveChanges = async () => {
    if (!canManage) {
      alert("Only the profile owner can modify these settings.");
      return;
    }
    if (!profileId) {
      window.alert("No active care profile selected.");
      return;
    }
    setIsSaving(true);
    try {
      const profileRef = doc(db, "careProfiles", profileId);
      await updateDoc(profileRef, { vitalsConfig });
      window.alert("Vitals configuration saved successfully!");
      navigate(`/vitals`);
    } catch (error) {
      console.error("Error saving vitals configuration:", error);
      window.alert("Failed to save configuration. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || authLoading)
    return <div className="text-center p-8">Loading configuration...</div>;
  if (!activeCareProfile)
    return (
      <div className="text-center p-8 text-red-500">
        Please select a care profile to manage vitals.
      </div>
    );

  const activeVitals = vitalsConfig.filter((v) => !v.archived);
  const archivedVitals = vitalsConfig.filter((v) => v.archived);

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-red-700">
            Manage Vitals Fields
          </h1>
          <p className="text-gray-600 mt-1">
            Customize vitals for {activeCareProfile.profileName}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate("/vitals")}
            className="w-full sm:w-auto px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg shadow-md hover:bg-gray-700 transition flex items-center justify-center gap-2"
          >
            <FaArrowLeft />
            Back to Vitals
          </button>
          <button
            onClick={handleSaveChanges}
            disabled={isSaving || !canManage}
            className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white font-semibold rounded-lg shadow-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
          >
            <FaSave />
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {!canManage && (
        <div
          className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6"
          role="alert"
        >
          <p className="font-bold">Read-Only Mode</p>
          <p>Only the profile owner has permission to modify these settings.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <fieldset
          disabled={!canManage}
          className="bg-white p-6 rounded-lg shadow-md border-0"
        >
          <legend className="text-xl font-bold mb-4 px-1">Active Vitals</legend>
          <div className="space-y-4">
            {activeVitals.map((vital) => (
              <div
                key={vital.id}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-3 bg-gray-50 rounded-lg"
              >
                {editingVitalId === vital.id ? (
                  <div className="flex-grow flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                    <input
                      type="text"
                      value={editingVitalName}
                      onChange={(e) => setEditingVitalName(e.target.value)}
                      className="block flex-grow rounded-md border-gray-300 shadow-sm"
                    />
                    <input
                      type="text"
                      value={editingVitalUnit}
                      onChange={(e) => setEditingVitalUnit(e.target.value)}
                      placeholder="Unit"
                      className="block w-full sm:w-24 rounded-md border-gray-300 shadow-sm"
                    />
                  </div>
                ) : (
                  <div className="flex-grow">
                    <span className="font-semibold text-gray-800">
                      {vital.name}
                    </span>
                    {vital.unit && (
                      <span className="text-sm text-gray-500 ml-2">
                        ({vital.unit})
                      </span>
                    )}
                  </div>
                )}
                <div className="flex flex-shrink-0 items-center gap-4">
                  {!vital.isDefault &&
                    (editingVitalId === vital.id ? (
                      <>
                        <button
                          onClick={handleUpdateVital}
                          className="text-green-500 hover:text-green-700"
                        >
                          <FaSave />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTimes />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEditClick(vital)}
                          disabled={!canManage}
                          className="text-blue-500 hover:text-blue-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleArchiveVital(vital.id)}
                          disabled={!canManage}
                          className="text-yellow-600 hover:text-yellow-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                          title="Archive"
                        >
                          <FaArchive />
                        </button>
                      </>
                    ))}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={vital.enabled}
                      onChange={() => handleToggleVital(vital.id)}
                      className="sr-only peer"
                      disabled={editingVitalId === vital.id || !canManage}
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-red-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </fieldset>

        <div>
          <fieldset
            disabled={!canManage}
            className="bg-white p-6 rounded-lg shadow-md mb-8 border-0"
          >
            <legend className="text-xl font-bold mb-4 px-1">
              Add Custom Vital
            </legend>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="newVitalName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="newVitalName"
                  value={newVitalName}
                  onChange={(e) => setNewVitalName(e.target.value)}
                  placeholder="e.g., Pain Level"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm disabled:bg-gray-100"
                />
              </div>
              <div>
                <label
                  htmlFor="newVitalUnit"
                  className="block text-sm font-medium text-gray-700"
                >
                  Unit (Optional)
                </label>
                <input
                  type="text"
                  id="newVitalUnit"
                  value={newVitalUnit}
                  onChange={(e) => setNewVitalUnit(e.target.value)}
                  placeholder="e.g., 1-10"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm disabled:bg-gray-100"
                />
              </div>
              <button
                onClick={handleAddVital}
                className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white font-semibold rounded-lg shadow-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                <FaPlus /> Add Vital
              </button>
            </div>
          </fieldset>

          {archivedVitals.length > 0 && (
            <fieldset
              disabled={!canManage}
              className="bg-white p-6 rounded-lg shadow-md border-0"
            >
              <legend className="text-xl font-bold mb-4 px-1">
                Archived Vitals
              </legend>
              <div className="space-y-3">
                {archivedVitals.map((vital) => (
                  <div
                    key={vital.id}
                    className="flex items-center justify-between p-3 bg-gray-100 rounded-lg"
                  >
                    <p className="flex-grow">
                      <span className="font-semibold text-gray-600">
                        {vital.name}
                      </span>
                      {vital.unit && (
                        <span className="text-sm text-gray-500 ml-2">
                          ({vital.unit})
                        </span>
                      )}
                    </p>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => handleReinstateVital(vital.id)}
                        className="text-green-600 hover:text-green-800 mr-4"
                        title="Reinstate"
                      >
                        <FaUndo />
                      </button>
                      <button
                        onClick={() => handleDeleteVitalPermanently(vital.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete Permanently"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </fieldset>
          )}
        </div>
      </div>
    </div>
  );
};

export default VitalsSettingsPage;
